import React from 'react';
import { Button, theme, ButtonProps } from 'antd';
import './FloatingPrimaryButton.css';
import { shadows } from '../../theme/tokens/shadows';

const { useToken } = theme;

/**
 * FloatingPrimaryButton
 * - Uses Ant `Button type="primary"` so it inherits your primary tokens (white bg / black text).
 * - Adds elevation + positioning for the "floating primary" pattern.
 *
 * Note: default placement is fixed bottom-right. Pages can override via `placement` + `offset`,
 * or pass `style` for custom positioning (absolute/static/etc).
 */
interface FloatingPrimaryButtonProps extends ButtonProps {
  placement?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
  offset?: number | string;
  badge?: string; // Optional yellow pill label
}

export default function FloatingPrimaryButton({
  placement = 'bottomRight',
  offset = 24,
  style,
  className,
  badge,
  ...buttonProps
}: FloatingPrimaryButtonProps) {
  const { token } = useToken() as { token: any };

  // Set z-index to be above drawer but below modal/sider overlays
  const defaultZIndex = 960;

  const placementClass = {
    bottomRight: 'bottom-right',
    bottomLeft: 'bottom-left',
    topRight: 'top-right',
    topLeft: 'top-left',
  }[placement];

  const offsetStyle = typeof offset === 'number' ? `${offset}px` : offset;
  const positionOverride: React.CSSProperties = {};

  if (offset !== 24) {
    switch (placement) {
      case 'bottomLeft':
        positionOverride.left = offsetStyle;
        positionOverride.bottom = offsetStyle;
        break;
      case 'topRight':
        positionOverride.right = offsetStyle;
        positionOverride.top = offsetStyle;
        break;
      case 'topLeft':
        positionOverride.left = offsetStyle;
        positionOverride.top = offsetStyle;
        break;
      case 'bottomRight':
      default:
        positionOverride.right = offsetStyle;
        positionOverride.bottom = offsetStyle;
        break;
    }
  }

  const wrapperStyle: React.CSSProperties = {
    position: 'fixed',
    ...positionOverride,
    ...style,
    zIndex: defaultZIndex,
  };

  return (
    <div style={wrapperStyle}>
      <div className="floating-button-container" style={{ position: 'relative', display: 'inline-block' }}>
        {badge && (
          <div className="floating-button-badge">
            {badge}
          </div>
        )}
        <Button
          type="primary"
          {...buttonProps}
          className={`floating-primary-button ${placementClass} ${className || ''}`}
          style={{
            boxShadow: token.boxShadow,
            height: '46px',
            position: 'relative',
            '--hover-shadow': shadows.m3Dark5,
          } as React.CSSProperties}
        />
      </div>
    </div>
  );
}
